import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { Users, ArrowUpRight, Check, Phone, PhoneCall, Clock, Shield, Sparkles, ChevronDown, ChevronUp, Mic, Video, Headphones, DollarSign } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Users, title: 'Multi-party calls', text: 'Connect multiple participants in a single conference call for team discussions and customer escalations.' },
  { icon: PhoneCall, title: 'Call combining', text: 'Merge different call types (voice, video, chat) into one conference for seamless communication.' },
  { icon: Mic, title: 'Audio controls', text: 'Mute, unmute, and manage participant audio with advanced controls for productive meetings.' },
  { icon: Shield, title: 'Secure connections', text: 'End-to-end encryption ensures your conference calls remain private and secure.' },
];

const useCases = [
  { title: 'Team discussions', text: 'Hold internal team meetings, stand-ups, and collaborative sessions without leaving the workspace.' },
  { title: 'Customer escalations', text: 'Bring multiple agents and supervisors into customer calls for complex issue resolution.' },
  { title: 'Sales demos', text: 'Conduct product demonstrations and sales presentations with multiple stakeholders.' },
  { title: 'Training sessions', text: 'Run training and onboarding sessions for new team members with built-in recording.' },
];

const conferenceTypes = [
  { type: 'Audio Conference', description: 'Traditional voice conference with multiple participants', icon: Phone },
  { type: 'Video Conference', description: 'Face-to-face video meetings with screen sharing', icon: Video },
  { type: 'Mixed Conference', description: 'Combine voice, video, and chat participants in one call', icon: Users },
];

const benefits = [
  { icon: Clock, title: 'Instant setup', description: 'Start conferences in seconds without scheduling or external tools.' },
  { icon: DollarSign, title: 'No extra cost', description: 'Conference calling is included free with Zuri Voice - no per-minute fees.' },
  { icon: Shield, title: 'Recording included', description: 'Record conferences for training, compliance, and review purposes.' },
  { icon: Sparkles, title: 'Integrated experience', description: 'Conferences live alongside tickets, chats, and customer history.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'How many participants can join a conference call?',
    answer: 'Conference calls support up to 50 participants by default. For larger conferences, contact our enterprise team for custom solutions that can accommodate hundreds of participants.'
  },
  {
    question: 'Is conference calling included in the pricing?',
    answer: 'Yes! Conference calling is completely free with Zuri Voice. There are no additional charges for hosting or joining conference calls. You only pay for standard voice minutes if using phone numbers.'
  },
  {
    question: 'Can I combine different call types in one conference?',
    answer: 'Absolutely. You can merge voice calls, video calls, and even chat participants into a single conference. This flexibility allows you to include team members regardless of their preferred communication method.'
  },
  {
    question: 'How do I start a conference call?',
    answer: 'Start a conference directly from any conversation thread. Click the conference button, invite participants by name or phone number, and begin the call instantly. No scheduling or external links required.'
  },
  {
    question: 'Can I record conference calls?',
    answer: 'Yes, conference calls can be recorded for training, compliance, and review. Recordings are stored securely and can be accessed within the conversation thread. Standard recording rates apply if enabled.'
  },
  {
    question: 'What audio controls are available during conferences?',
    answer: 'Hosts have full control over the conference including mute/unmute participants, remove attendees, lock the conference, and manage speaking order. Participants can mute themselves and raise their hand to speak.'
  },
  {
    question: 'Are conference calls secure?',
    answer: 'All conference calls use end-to-end encryption and are hosted on our secure infrastructure. Access controls ensure only invited participants can join, and optional PIN protection adds an extra layer of security.'
  },
  {
    question: 'Can I schedule conference calls in advance?',
    answer: 'Yes, you can schedule conference calls and send calendar invitations to participants. Scheduled conferences appear in your workspace and can be started with one click when the time arrives.'
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Conferences() {
  return (
    <>
      <SEO
        title="Conferences - Multi-Party Calling | Zuri Voice"
        description="Connect people in multiparty calls or combine all types of calls. Ideal for team discussions, customer escalations, and sales demos. Included free with Zuri Voice."
        path="/conferences"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Users className="h-4 w-4" /> Included free
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Conference calls that <span className="gradient-text">bring teams together</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Connect people in multiparty calls or combine all types of calls. Ideal for team discussions, customer escalations, and sales demos. No extra cost.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/zuri-voice"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Back to Zuri Voice <Users className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Conference.png"
                  alt="Conference calling dashboard showing multi-party call features and controls"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful conference features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need for productive multi-party calls.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="types-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="types-heading" className="section-title">Conference types</h2>
            <p className="section-subtitle mx-auto mt-4">Flexible options for every communication need.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {conferenceTypes.map((type) => (
              <div key={type.type} className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <type.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{type.type}</h3>
                <p className="mt-2 text-sm text-slate-600">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why use Zuri conferences?</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform team collaboration.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Start conference calls instantly from your workspace with intuitive controls and seamless integration.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Users,
                    title: 'Start a conference',
                    description: 'Click the conference button from any conversation or create a new conference from the dashboard.',
                  },
                  {
                    icon: PhoneCall,
                    title: 'Invite participants',
                    description: 'Add team members by name or dial phone numbers to bring external participants into the call.',
                  },
                  {
                    icon: Headphones,
                    title: 'Manage the call',
                    description: 'Use host controls to mute, remove, or manage participants. Record the call if needed.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Conference controls</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Mute/unmute any participant</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Remove attendees from call</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Lock conference to prevent new joins</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Raise hand feature for participants</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Recording and transcription</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Screen sharing (video conferences)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about conference calling</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}